import React, { createContext, useContext, useState, useCallback, useMemo, useEffect, useLayoutEffect } from 'react'
import { useCache } from '@fs/zion-cache'
import { trackEvent } from '@fs/zion-analytics'
import useRootsTechVisibility from './useRootsTechVisibility'

const RootsTechBannerContext = createContext()
const cache = { storeName: 'mediaBanners' }

export const useRootsTechBannerContext = () => {
  return useContext(RootsTechBannerContext)
}

export default function RootsTechBannerProvider({ startExpandedVisibility, analyticsName, cacheKey, children }) {
  const { shouldDisplay: shouldStartExpanded } = useRootsTechVisibility(startExpandedVisibility, false)
  const [cachedData, setCachedData] = useCache(
    { cache, key: `rootsTechBanner-isOpen-${cacheKey || analyticsName}` },
    { isOpen: null }
  )
  const [finishedPainting, setFinishedPainting] = useState(false)

  const [hasExpandedView, setHasExpandedView] = useState(false)
  const [hasCollapsedView, setHasCollapsedView] = useState(false)
  const canToggle = hasExpandedView && hasCollapsedView

  const [isOpen, setIsOpen] = useState()

  useEffect(() => {
    if (canToggle) {
      setIsOpen(cachedData?.isOpen ?? shouldStartExpanded)
    }
  }, [canToggle, shouldStartExpanded, cachedData])

  useLayoutEffect(() => {
    // we care about finished painting because we want to know if the children finished rendering to determine if we canToggle or not before analytics is triggered
    setFinishedPainting(true)
  }, [])

  const toggleBanner = useCallback(() => {
    setIsOpen((prev) => {
      const newState = !prev
      setCachedData({ isOpen: newState })
      trackEvent({
        event_name: `RT_Banner: ${analyticsName}: ${newState ? 'Expanded' : 'Collapsed'} Click`,
      })
      return newState
    })
  }, [setCachedData, analyticsName])

  const registerExpandedView = useCallback(() => {
    setHasExpandedView(true)
  }, [])

  const registerCollapsedView = useCallback(() => {
    setHasCollapsedView(true)
  }, [])

  const value = useMemo(
    () => ({
      canToggle,
      registerExpandedView,
      registerCollapsedView,
      isOpen,
      toggleBanner,
      analyticsName,
      finishedPainting,
      hasExpandedView,
      hasCollapsedView,
    }),
    [
      canToggle,
      registerExpandedView,
      registerCollapsedView,
      isOpen,
      toggleBanner,
      analyticsName,
      finishedPainting,
      hasExpandedView,
      hasCollapsedView,
    ]
  )

  return <RootsTechBannerContext.Provider value={value}>{children}</RootsTechBannerContext.Provider>
}
